import React, { useEffect, useState } from 'react';
import { useSelector } from "react-redux";
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';
import ImageComponent from '../../../components/common/Image';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../../common/Modal';

const DealerListContent = ({ searchTerm, setSearchTerm, showSearchModal, setShowSearchModal }) => {

    const href = "#";
    const navigate = useNavigate();
    const { userData } = useSelector((state) => state.auth);
    const dealerData = userData?.response_data?.dealer_data || [];

    const [selectedDealerCode, setSelectedDealerCode] = useState('all');
    const [selectedDealerName, setSelectedDealerName] = useState('All');
    const [tempSelectedDealerCode, setTempSelectedDealerCode] = useState(selectedDealerCode);
    const [tempSelectedDealerName, setTempSelectedDealerName] = useState(selectedDealerName);
    const [showDealerModal, setShowDealerModal] = useState(false);
    const [showDealerInfoModal, setShowDealerInfoModal] = useState(false);
    const [selectedDealer, setSelectedDealer] = useState(null);
    const [displayedDealerCount, setDisplayedDealerCount] = useState(4);
    const [dealerSearchTerm, setDealerSearchTerm] = useState('');

    const dealersPerPage = 4;

    // Function to handle dealer selection
    const handleDealerSelection = (dealerCode, dealerName) => {
        setTempSelectedDealerCode(dealerCode);
        setTempSelectedDealerName(dealerName);
    };

    const handleApplyDealer = () => {
        setSelectedDealerCode(tempSelectedDealerCode);
        setSelectedDealerName(tempSelectedDealerName);
        setShowDealerModal(false);
        // Reset search when dealer filter is applied
        setDealerSearchTerm('');
    };

    // Function to load more dealers
    const loadMoreDealers = () => {
        setDisplayedDealerCount(prev => prev + dealersPerPage);
    };

    // Filter dealers based on search term and selected dealer
    const filteredDealers = dealerData?.filter((dealer) => {
        // First filter by selected dealer if not 'all'
        const matchesSelectedDealer = selectedDealerCode === 'all' || dealer?.dealercode === selectedDealerCode;

        // Then filter by search term
        const matchesSearch = !dealerSearchTerm ||
            dealer?.dealercode?.toLowerCase().includes(dealerSearchTerm.toLowerCase()) ||
            dealer?.dealername?.toLowerCase().includes(dealerSearchTerm.toLowerCase()) ||
            dealer?.dealeraddress?.toLowerCase().includes(dealerSearchTerm.toLowerCase());

        return matchesSelectedDealer && matchesSearch;
    });

    // Reset displayed dealer count when dealer data, search, or selected dealer changes
    useEffect(() => {
        setDisplayedDealerCount(dealersPerPage);
    }, [dealerData, dealerSearchTerm, selectedDealerCode]);

    const handleNavigateToDashboard = () => {
        navigate('/dashboard');
    };

    // Function to handle dealer info modal
    const handleDealerInfoClick = (dealer) => {
        setSelectedDealer(dealer);
        setShowDealerInfoModal(true);
    };

    return (
        <>
            <div className="container-fluid kyc-tab-dash dealer-tab-dash">
                <div className="dash-tab-sroll">
                    <div className="row">
                        <div className="col-12">
                            <div className="d-flex align-items-center gap-2">
                                <div className="has-icon w-100">
                                    <input
                                        className="form-control"
                                        placeholder="Search Stores"
                                        type="text"
                                        value={dealerSearchTerm}
                                        onChange={(e) => setDealerSearchTerm(e.target.value)}
                                    />
                                    <i
                                        className="icon-settingh dealer-list-filter"
                                        onClick={() => setShowDealerModal(true)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && setShowDealerModal(true)}
                                        aria-label="Filter stores"
                                    ></i>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="row gy-4 mt-1">
                        {filteredDealers?.length > 0 ? (
                            filteredDealers?.slice(0, displayedDealerCount).map((dealer, index) => {
                                // Extract city from address (last part before state)
                                const addressParts = dealer?.dealeraddress?.split(',');
                                const city = addressParts?.length > 2 ? addressParts[addressParts?.length - 3]?.trim() : 'Location';

                                return (
                                    <div
                                        key={index}
                                        className="col-12 col-lg-12 col-md-12 dealer-list-col"
                                        onClick={() => handleDealerInfoClick(dealer)}
                                        role="button"
                                        tabIndex={0}
                                        onKeyDown={(e) => e.key === 'Enter' && handleDealerInfoClick(dealer)}
                                        aria-label="Store Info"
                                    >
                                        <div className="product-details px-3 py-3">
                                            <div className="d-flex align-items-center gap-3">
                                                <div className="lh-sm">
                                                    <p className="fw-semibold pt-0 mx-0">{dealer?.dealercode} - {dealer?.dealername}</p>
                                                    <div className="d-flex gap-2 mt-2">
                                                        <p className="small d-none dark-blue store-format py-2 mx-0"><i className="icon-Contentnote-text mx-1"></i>CD/DP</p>
                                                        <p className="small dark-blue store-format py-2 mx-0"><i className="icon-location mx-1"></i>{city}</p>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })
                        ) : (
                            <div className="col-12 col-lg-12 col-md-12">
                                <div className="product-details px-3 py-3 text-center">
                                    <p className="text-secondary">
                                        {dealerSearchTerm ? 'No stores found matching your search' : 'No stores available'}
                                    </p>
                                </div>
                            </div>
                        )}

                        <div className="col-md-12 text-center">
                            {filteredDealers?.length > dealersPerPage && displayedDealerCount < filteredDealers?.length && (
                                <button
                                    className="dark-blue small load-more"
                                    onClick={loadMoreDealers}
                                >
                                    load more
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            {/* Stores Modal */}
            <Modal
                id="SelectRole"
                show={showDealerModal}
                onClose={() => setShowDealerModal(false)}
                modalClass="modal-dialog bottom-modal"
                contentClass="blue"
            >
                <ModalHeader className="">
                    <div className="top-draw"></div>
                </ModalHeader>

                <div className="d-flex align-items-center justify-content-between px-4 py-3 w-100">
                    <h6 className="mb-1">Stores</h6>
                </div>

                <ModalBody>
                    <ul className="list-group list-group-flush lh-lg ul-height">
                        <li
                            className={`list-group-item border-0 text-secondary ${tempSelectedDealerCode === 'all' ? 'dealer-selected' : ''}`}
                            onClick={() => handleDealerSelection('all', 'All')}
                            onKeyDown={(e) => e.key === 'Enter' && handleDealerSelection('all', 'All')}
                            tabIndex={0}
                            role="button"
                        >
                            All Stores
                        </li>
                        {dealerData?.length > 0 ? (
                            dealerData?.map((dealer, index) => (
                                <li
                                    key={index}
                                    className={`list-group-item border-0 text-secondary ${tempSelectedDealerCode === dealer?.dealercode ? 'dealer-selected' : ''}`}
                                    onClick={() => handleDealerSelection(dealer?.dealercode, dealer?.dealername)}
                                    onKeyDown={(e) => e.key === 'Enter' && handleDealerSelection(dealer?.dealercode, dealer?.dealername)}
                                    tabIndex={0}
                                    role="button"
                                >
                                    {dealer?.dealercode} - {dealer?.dealername}
                                </li>
                            ))
                        ) : (
                            <li className="list-group-item border-0 text-secondary">
                                No stores available
                            </li>
                        )}
                    </ul>
                </ModalBody>

                <ModalFooter>
                    <button
                        type="button"
                        className="btn btn-primary w-100"
                        onClick={handleApplyDealer}
                    >
                        Apply
                    </button>
                </ModalFooter>
            </Modal>

            {/* Dealer Info Modal */}
            <Modal
                id="dealerInfoModal"
                show={showDealerInfoModal}
                onClose={() => setShowDealerInfoModal(false)}
                modalClass="modal-dialog bottom-modal"
                contentClass="blue"
            >

                <ModalHeader className="mt-3">
                    <div className="top-draw"></div>
                </ModalHeader>

                <ModalBody>
                    {selectedDealer && (
                        <>
                            <p className="fw-bold">{selectedDealer?.dealercode} - {selectedDealer?.dealername}</p>

                            <div className="d-flex align-items-baseline gap-2">
                                <span className="icon-locationrouting icon-clr"></span>
                                <p><a href={href} className="text-secondary">{selectedDealer?.dealeraddress}</a></p>
                            </div>
                            <div className="d-flex d-none align-items-baseline gap-2">
                                <span className="icon-timecalendar-2"></span>
                                <p>Monday to Friday (9AM -9PM)</p>
                            </div>

                            <div className="d-flex d-none align-items-baseline gap-2">
                                <span className="icon-Contenttask"></span>
                                <p>Consumer Durables/Digital Products</p>
                            </div>
                        </>
                    )}
                </ModalBody>
            </Modal>
        </>
    )
}

DealerListContent.propTypes = {
    searchTerm: PropTypes.string.isRequired,
    setSearchTerm: PropTypes.func.isRequired,
    showSearchModal: PropTypes.bool.isRequired,
    setShowSearchModal: PropTypes.func.isRequired
};

export default DealerListContent